/* eslint-disable react-refresh/only-export-components -- Context files intentionally export both a context object and a hook from the same module. Splitting would break the encapsulation of this pattern. */
import { createContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  signIn,
  signOut,
  signInWithRedirect,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
} from 'aws-amplify/auth';
import type { SignInWithRedirectInput } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { createMember } from '@/api';
import { useMixpanel } from './MixpanelContext';
import type { User } from '@/types';

export type SocialProvider = SignInWithRedirectInput['provider'];

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  socialLogin: (provider: SocialProvider) => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // Get Mixpanel from context (injected via MixpanelProvider)
  const { mixpanel } = useMixpanel();

  const checkAuthState = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();

      if (currentUser && session.tokens) {
        // Fetch user attributes to get the current email (not the login ID which doesn't update)
        let email = currentUser.signInDetails?.loginId || '';
        try {
          const attributes = await fetchUserAttributes();
          if (attributes.email) {
            email = attributes.email;
          }
        } catch (attrError) {
          console.warn('Failed to fetch user attributes, using loginId:', attrError);
        }

        const user: User = {
          id: currentUser.userId,
          email,
          memberType: 'creator',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        // Identify user in Mixpanel for returning sessions
        // This ensures users who return with an existing Cognito session are tracked
        mixpanel.identifyUser(user.id, { $email: email });

        setState({
          user,
          isAuthenticated: true,
          isLoading: false,
        });
      } else {
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    } catch {
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  }, [mixpanel]);

  useEffect(() => {
    void checkAuthState();
  }, [checkAuthState]);

  // Listen for Amplify Hub auth events to handle social login redirects.
  // When signInWithRedirect completes, Cognito redirects back to the app
  // with an auth code. Amplify exchanges it for tokens and fires 'signInWithRedirect'
  // on the Hub. We then check auth state to update the UI.
  useEffect(() => {
    const unsubscribe = Hub.listen('auth', async ({ payload }) => {
      switch (payload.event) {
        case 'signInWithRedirect':
          // Social login completed successfully — refresh auth state
          await checkAuthState();
          break;
        case 'signInWithRedirect_failure':
          console.error('Social sign-in redirect failed:', payload.data);
          setState((prev) => ({ ...prev, isLoading: false }));
          break;
      }
    });

    return unsubscribe;
  }, [checkAuthState]);

  const login = async (email: string, password: string) => {
    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      await signIn({
        username: email,
        password,
        options: {
          authFlowType: 'USER_PASSWORD_AUTH',
        },
      });
      await checkAuthState();

      // Identify user in Mixpanel and track login event
      const currentUser = await getCurrentUser();
      if (currentUser) {
        mixpanel.identifyUser(currentUser.userId, { $email: email });
        mixpanel.event('login_completed', { provider: 'email' });
      }
    } catch (error) {
      setState((prev) => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  /**
   * Register a new user via the Member Gateway service.
   *
   * The Cognito user pool doesn't allow direct sign-ups - users must be
   * created through the Member Gateway service which handles Cognito user
   * creation via Admin APIs. The Member Gateway creates users with confirmed
   * status, so no email verification step is needed.
   *
   * After creation, we automatically sign in the user.
   */
  const register = async (email: string, password: string) => {
    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      // Create the member via Member Gateway (handles Cognito user creation)
      await createMember(email, password);

      // Auto sign-in after successful registration
      // Using USER_PASSWORD_AUTH flow as required by the Cognito pool configuration
      const { isSignedIn } = await signIn({
        username: email,
        password,
        options: {
          authFlowType: 'USER_PASSWORD_AUTH',
        },
      });

      if (isSignedIn) {
        await checkAuthState();

        // Identify user in Mixpanel and track signup event
        const currentUser = await getCurrentUser();
        if (currentUser) {
          mixpanel.identifyUser(currentUser.userId, { $email: email });
          mixpanel.event('sign_up_completed', {
            provider: 'email',
            country: '', // Will be set when user adds address
            email,
            push_enabled: false,
            email_enabled: true,
          });
        }
      }
    } catch (error) {
      setState((prev) => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  /**
   * Initiate social login via Cognito Hosted UI.
   *
   * This triggers a redirect to the Cognito OAuth endpoint which then
   * redirects to the social provider (Google, Apple, Facebook). After
   * authentication, the user is redirected back to the app. The Hub
   * listener above detects the completed sign-in and updates auth state.
   *
   * Mirrors the v4-mobile implementation in cognitoAuth.ts which uses
   * signInWithRedirect({ provider }) from aws-amplify/auth.
   */
  const socialLogin = async (provider: SocialProvider) => {
    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      // Reset Mixpanel identity before signing out to prevent events from
      // being associated with the previous user during the redirect flow.
      // This is consistent with the logout() method.
      mixpanel.reset();

      // Sign out any existing session before starting social login
      // This matches the v4-mobile pattern in socialSignin()
      await signOut();

      // Encode the current origin into Cognito's state parameter via customState.
      // The Auth Hub Worker (auth-hub/src/index.ts) extracts this to redirect
      // back to the correct PR preview environment after OAuth completes.
      // encodeURIComponent is required because the Auth Hub calls
      // decodeURIComponent() when extracting the return URL from the state.
      // For QA/prod this is harmless — the redirect goes directly to the app.
      const customState = encodeURIComponent(window.location.origin);
      await signInWithRedirect({ provider, customState });
      // Note: The browser will redirect away at this point.
      // When it returns, the Hub listener will handle checkAuthState()
      // and Mixpanel tracking.
    } catch (error) {
      setState((prev) => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  const logout = async () => {
    // Reset Mixpanel user identity before signing out
    mixpanel.reset();

    await signOut();
    setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
    });
  };

  const refreshSession = async () => {
    await checkAuthState();
  };

  const value: AuthContextType = {
    ...state,
    login,
    register,
    socialLogin,
    logout,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
